import React from 'react'
import { Divider, Progress, Spin } from 'antd'
import { Link } from 'react-router-dom'
import ReactApexChart from 'react-apexcharts'
import { useGetDashboardData } from 'src/cms/hooks/Individual/useGetDashboardData'
import { FaFileAlt, FaFileArchive } from 'react-icons/fa'
import { MdCancel } from 'react-icons/md'
import { AiOutlineFileDone } from 'react-icons/ai'
import { useSelector } from 'react-redux'
import './style/DashBoardCard.scss'

const humanizeSlug = (slug: string) =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const getCompletionRate = (total: number, completed: number) =>
  total > 0 ? Math.round((completed / total) * 100) : 0

const buildBarOptions = (): ApexCharts.ApexOptions => ({
  chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: true } },
  plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 4 } },
  colors: ['#10b981', '#3b82f6', '#f59e0b'],
  xaxis: { categories: ['Completed', 'In Progress', 'Cancelled'] },
  dataLabels: { enabled: true, style: { fontSize: '11px' } },
  grid: { borderColor: '#f1f1f1' },
  tooltip: { y: { formatter: (val: number) => `${val} cases` } },
  legend: { show: true, position: 'top', fontSize: '12px' },
})

const buildGroupedBarSeries = (
  accountTypes: [string, {Completed: number; Cancelled: number; 'cde-init': number}][]
) =>
  accountTypes.map(([typeName, typeStats]) => ({
    name: typeName,
    data: [
      Number(typeStats?.Completed) || 0,
      Number(typeStats?.['cde-init']) || 0,
      Number(typeStats?.Cancelled) || 0,
    ],
  }))

const buildDonutOptions = (): ApexCharts.ApexOptions => ({
  chart: { type: 'donut' },
  labels: ['Completed', 'In Progress', 'Cancelled'],
  colors: ['#10b981', '#3b82f6', '#ef5350'],
  legend: { position: 'bottom', fontSize: '12px' },
  plotOptions: { pie: { donut: { size: '65%' } } },
  dataLabels: { enabled: false },
  tooltip: { y: { formatter: (val: number) => `${val} cases` } },
})

const CardComponent = () => {
  const { data: DashboardData, isLoading } = useGetDashboardData('NewCIFNewAccount')
  const roleName = useSelector((state: any) => state.auth?.rolePermissions?.name)

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Spin tip='Loading...' size='large' fullscreen />
      </div>
    )
  }

  const totalCount = Number(DashboardData?.data?.total_count) || 0
  const totalCompleted = Number(DashboardData?.data?.total_completed) || 0
  const totalRejected = Number(DashboardData?.data?.total_rejected) || 0
  const totalInProgress = totalCount - totalCompleted - totalRejected
  const completionRate = getCompletionRate(totalCount, totalCompleted)
  const dashboard = DashboardData?.data?.dashboard || {}

  return (
    <div className='mt-4'>
      {/* ── Summary Section ── */}
      <section className='mb-4'>
        <div className='d-flex align-items-center mb-1'>
          <h3 className='title mb-0'>Branch Details</h3>
          <span className='completion-badge'>{completionRate}% Completion Rate</span>
        </div>
        <Divider className='m-0 mb-3' />

        <div className='row'>
          <div className='col-lg-3 col-md-6 my-2'>
            <Link
              to={
                roleName?.toLowerCase()?.includes('ro')
                  ? '/quick-account-opening'
                  : '/account-opening-status'
              }
            >
              <div className='stat-card stat-card-amber'>
                <div className='stat-card-left'>
                  <p className='stat-card-label'>Total Cases</p>
                  <h2 className='stat-card-count'>{totalCount}</h2>
                </div>
                <div className='stat-card-icon'>
                  <FaFileAlt size={28} />
                </div>
              </div>
            </Link>
          </div>

          <div className='col-lg-3 col-md-6 my-2'>
            <Link
              to={{
                pathname: roleName?.toLowerCase()?.includes('ro')
                  ? '/quick-account-opening'
                  : '/new-cif-new-account',
                state: roleName?.toLowerCase()?.includes('ro')
                  ? '/completedAccount'
                  : 'completedIndividual',
              }}
            >
              <div className='stat-card stat-card-green'>
                <div className='stat-card-left'>
                  <p className='stat-card-label'>Completed</p>
                  <h2 className='stat-card-count'>{totalCompleted}</h2>
                </div>
                <div className='stat-card-icon'>
                  <AiOutlineFileDone size={28} />
                </div>
              </div>
            </Link>
          </div>

          {!roleName?.toLowerCase()?.includes('ro') && (
            <div className='col-lg-3 col-md-6 my-2'>
              <Link to='/new-cif-new-account/rejected-account-lists'>
                <div className='stat-card stat-card-red'>
                  <div className='stat-card-left'>
                    <p className='stat-card-label'>Rejected</p>
                    <h2 className='stat-card-count'>{totalRejected}</h2>
                  </div>
                  <div className='stat-card-icon'>
                    <MdCancel size={28} />
                  </div>
                </div>
              </Link>
            </div>
          )}

          <div className='col-lg-3 col-md-6 my-2'>
            <div className='stat-card stat-card-blue'>
              <div className='stat-card-left'>
                <p className='stat-card-label'>In Progress</p>
                <h2 className='stat-card-count'>{totalInProgress}</h2>
              </div>
              <div className='stat-card-icon'>
                <FaFileArchive size={28} />
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-completion-bar'>
          <Progress
            percent={completionRate}
            strokeColor={{ '0%': '#f59e0b', '100%': '#10b981' }}
            status='active'
          />
        </div>
      </section>

      {/* ── Per-Product Sections ── */}
      {Object.entries(dashboard).map(([slug, stats]: [string, any]) => {
        const completed = Number(stats?.completed) || 0
        const cancelled = Number(stats?.cancelled) || 0
        const total = Number(stats?.total) || 0
        const inProgress = total - completed - cancelled
        const hasData = completed + inProgress + cancelled > 0

        const accountTypes = Object.entries(stats).filter(
          ([, val]) => typeof val === 'object' && val !== null
        ) as [string, {Completed: number; Cancelled: number; 'cde-init': number}][]

        return (
          <section key={slug} className='mb-5'>
            <h3 className='title'>{humanizeSlug(slug)}</h3>
            <Divider className='m-0 mb-3' />

            {/* Stat cards */}
            <div className='row'>
              <div className='col-lg-3 col-md-6 my-2'>
                <div className='stat-card stat-card-amber'>
                  <div className='stat-card-left'>
                    <p className='stat-card-label'>Total</p>
                    <h2 className='stat-card-count'>{total}</h2>
                  </div>
                  <div className='stat-card-icon'>
                    <FaFileAlt size={28} />
                  </div>
                </div>
              </div>

              <div className='col-lg-3 col-md-6 my-2'>
                <div className='stat-card stat-card-green'>
                  <div className='stat-card-left'>
                    <p className='stat-card-label'>Completed</p>
                    <h2 className='stat-card-count'>{completed}</h2>
                  </div>
                  <div className='stat-card-icon'>
                    <AiOutlineFileDone size={28} />
                  </div>
                </div>
              </div>

              <div className='col-lg-3 col-md-6 my-2'>
                <div className='stat-card stat-card-red'>
                  <div className='stat-card-left'>
                    <p className='stat-card-label'>Cancelled</p>
                    <h2 className='stat-card-count'>{cancelled}</h2>
                  </div>
                  <div className='stat-card-icon'>
                    <MdCancel size={28} />
                  </div>
                </div>
              </div>

              <div className='col-lg-3 col-md-6 my-2'>
                <div className='stat-card stat-card-blue'>
                  <div className='stat-card-left'>
                    <p className='stat-card-label'>In Progress</p>
                    <h2 className='stat-card-count'>{inProgress}</h2>
                  </div>
                  <div className='stat-card-icon'>
                    <FaFileArchive size={28} />
                  </div>
                </div>
              </div>
            </div>

            {/* Account-type breakdown (Individual / Joint / Minor) */}
            {accountTypes.length > 0 && (
              <div className='row mt-2'>
                {accountTypes.map(([typeName, typeStats]) => {
                  const typeCompleted = Number(typeStats?.Completed) || 0
                  const typeCancelled = Number(typeStats?.Cancelled) || 0
                  const typeInProgress = Number(typeStats?.['cde-init']) || 0
                  const typeTotal = typeCompleted + typeCancelled + typeInProgress
                  return (
                    <div key={typeName} className='col-lg-4 col-md-6 my-2'>
                      <div className='account-type-card'>
                        <div className='account-type-header'>
                          <span className='account-type-name'>{typeName}</span>
                          <span className='account-type-total'>Total: {typeTotal}</span>
                        </div>
                        <div className='account-type-stats'>
                          <div className='account-type-stat stat-completed'>
                            <span className='stat-val'>{typeCompleted}</span>
                            <span className='stat-lbl'>Completed</span>
                          </div>
                          <div className='account-type-stat stat-inprogress'>
                            <span className='stat-val'>{typeInProgress}</span>
                            <span className='stat-lbl'>In Progress</span>
                          </div>
                          <div className='account-type-stat stat-cancelled'>
                            <span className='stat-val'>{typeCancelled}</span>
                            <span className='stat-lbl'>Cancelled</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Charts — only when there is data */}
            {hasData && (
              <div className='row mt-3'>
                <div className='col-lg-7 col-md-12 mb-3'>
                  <div className='dashboard-chart-card'>
                    <h6 className='chart-title'>Status Breakdown</h6>
                    <ReactApexChart
                      type='bar'
                      series={
                        accountTypes.length > 0
                          ? buildGroupedBarSeries(accountTypes)
                          : [{ name: 'Cases', data: [completed, inProgress, cancelled] }]
                      }
                      options={buildBarOptions()}
                      height={250}
                    />
                  </div>
                </div>

                <div className='col-lg-5 col-md-12 mb-3'>
                  <div className='dashboard-chart-card'>
                    <h6 className='chart-title'>Distribution</h6>
                    <ReactApexChart
                      type='donut'
                      series={[completed, inProgress, cancelled]}
                      options={buildDonutOptions()}
                      height={220}
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

export default CardComponent
